import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBulkTaxonomyGrid } from "@/components/settings/bulkTaxonomy/useBulkTaxonomyGrid";
import BulkTaxonomyToolbar from "@/components/settings/bulkTaxonomy/BulkTaxonomyToolbar";
import BulkTaxonomyGrid from "@/components/settings/bulkTaxonomy/BulkTaxonomyGrid";
import BulkTaxonomyFooter from "@/components/settings/bulkTaxonomy/BulkTaxonomyFooter";

export default function BulkTaxonomyDialog({ open, onOpenChange, onImported }) {
  const { user } = useAuth();
  const g = useBulkTaxonomyGrid({ open, onImported, onOpenChange });
  const rowRefs = useRef({});

  const scrollToFirstError = () => {
    if (g.firstErrorRow < 0) return;
    const el = rowRefs.current[g.firstErrorRow];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-rose-300");
      setTimeout(() => el.classList.remove("ring-2", "ring-rose-300"), 1400);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1500px] max-h-[92vh] flex flex-col" data-testid="bulk-taxonomy-dialog">
        <DialogHeader className="pb-2">
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-500" /> Bulk import — Categories &amp; Issues
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Paste from Excel / Google Sheets directly into the grid, or type rows manually. Click <strong>Validate</strong> to preview what'll happen, then <strong>Import</strong> to commit.
          </DialogDescription>
        </DialogHeader>

        <BulkTaxonomyToolbar
          isSuperAdmin={!!user?.is_super_admin}
          summary={g.summary}
          mode={g.mode} setMode={g.setMode}
          partial={g.partial} setPartial={g.setPartial}
          autoCreate={g.autoCreate} setAutoCreate={g.setAutoCreate}
          busy={g.busy} canImport={g.canImport}
          onLoadCurrent={g.loadCurrent}
          onClearAll={g.clearAll}
          onAddRow={g.addRow}
          onValidate={g.validate}
          onImport={g.runImport}
        />

        <BulkTaxonomyGrid
          rows={g.rows}
          depts={g.depts}
          caseTypes={g.caseTypes}
          errorsByRow={g.errorsByRow}
          statusByRow={g.statusByRow}
          onUpdateCell={g.updateCell}
          onDeleteRow={g.deleteRow}
          onAddRow={g.addRow}
          onPaste={g.onPaste}
          registerRow={(i, el) => { if (el) rowRefs.current[i] = el; }}
        />

        <BulkTaxonomyFooter
          rowCount={g.rows.length}
          summary={g.summary}
          onScrollToError={scrollToFirstError}
          onDownload={g.downloadCurrent}
        />
      </DialogContent>
    </Dialog>
  );
}
